import { forwardRef, QueryList } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { TooltipDirective } from '../shared/components/tooltip';
import { LiveAnnouncer } from '@angular/cdk/a11y';

export const FOCUSABLE_ELEMS =
  'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable], audio[controls], video[controls], summary';

export class TemplateUuids {
  uuids: { [key: string]: any } = {};

  get(id: number | string) {
    if (typeof id === 'number') {
      id = String(id);
    }

    if (!this.uuids[id]) {
      this.uuids[id] = 'g' + uuidv4();
    }

    return this.uuids[id];
  }
}

export function provideValueAccessor(component: any): any {
  return {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => component),
    multi: true,
  };
}

export function getFocusableElements(context?: string | Element) {
  let contextElement : Document | Element | null;

  if (!context) {
    contextElement = document;
  } else if (typeof context === 'string') {
    contextElement = document.querySelector(context);
  } else {
    contextElement = context;
  }

  if (!contextElement) {
    return [];
  }

  const focusableElements = Array.from((contextElement as HTMLElement).querySelectorAll(FOCUSABLE_ELEMS)) as HTMLElement[];
  // Filter out elements that are not visible (e.g., display: none)
  const visibleFocusableElements = focusableElements.filter(isElementVisible);
  return visibleFocusableElements;
}

export function isElementFocusable(element: HTMLElement) {
  return FOCUSABLE_ELEMS.includes(element.tagName.toLowerCase());
}

export function isIframeClick(event: Event) {
  return event && event.target instanceof Window;
}

export interface KebabElements {
  kebabButton: HTMLElement | null;
  kebabIcon: HTMLElement | null;
  kebabMenu: HTMLElement | null;
}

export function getKebabElements(target: EventTarget | null): KebabElements {
  const element = target as HTMLElement;
  return {
    kebabButton: element.closest('.btn-icon'),
    kebabIcon: element.closest('.ahe-icon-system-kebab'),
    kebabMenu: element.closest('.ahe-ui-dropdown')
  };
}

export function closeOpenTooltips(tooltips: QueryList<TooltipDirective>) {
  tooltips.forEach((tooltip) => {
    if (tooltip.isOpen()) {
      tooltip.close();
    }
  });
}

/**
 * Checks if an element is visible in the DOM.
 * An element is considered visible if it is connected to the DOM, does not have display: none or visibility: hidden, and has visible bounding rectangles.
 * @param element The HTMLElement to check for visibility.
 * @returns True if the element is visible, false otherwise.
 */
function isElementVisible(element: HTMLElement): boolean {
  // Check if the element is connected to the DOM
  if (!element.isConnected) return false;

  // Check if the element or any of its ancestors have display: none or visibility: hidden
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;

  // Check if the element has any visible bounding rectangles
  return element.getClientRects().length > 0;
}

/**
 * Announces a message to screen readers with a delay.
 * When announcement and focus change occur simultaneously, VoiceOver skips the announcement in favor of reading the focused element.
 * Adding a delay ensures the announcement is read after the focused element.
 *
 * @param liveAnnouncer The LiveAnnouncer instance used to announce messages.
 * @param message The message to announce.
 * @param politeness The politeness of the announcer element (default: polite).
 * @param duration Time in milliseconds after which to clear out the announcer element (default: 8000ms).
 * @param delayMs Delay in milliseconds before announcement (default: 0ms).
 */
export function queueLiveAnnouncement(liveAnnouncer: LiveAnnouncer, message: string, politeness: 'polite' | 'assertive' = 'polite', duration: number = 8000, delayMs: number = 0): void {
  liveAnnouncer.clear();
  setTimeout(() => {
    liveAnnouncer.announce(message, politeness, duration);
  }, delayMs);
}
